import React, {
  createContext, useCallback, useContext, useEffect,
  useMemo, useRef, useState,
} from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MetaPixelContext = createContext(null);

/** Inject the standard Meta Pixel bootstrap once, then `init` with the pixel id. */
const injectPixelScript = (pixelId) => {
  if (typeof window === "undefined" || !pixelId) return;
  const initFlag = `__hk_fbq_init_${pixelId}`;
  if (window.fbq) {
    if (!window[initFlag]) {
      try { window.fbq("init", pixelId); } catch {}
      window[initFlag] = true;
    }
    return;
  }
  /* eslint-disable */
  !function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = "2.0";
    n.queue = []; t = b.createElement(e); t.async = !0;
    t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  }(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
  try { window.fbq("init", pixelId); } catch {}
  window[initFlag] = true;
};

export const MetaPixelProvider = ({ children }) => {
  const [config, setConfig] = useState({ enabled: false, pixel_id: "" });
  const [ready, setReady] = useState(false);
  const location = useLocation();
  const lastPathRef = useRef(null);

  // Load config once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/settings/meta_pixel`);
        if (cancelled) return;
        const cfg = r.data || {};
        setConfig({
          enabled: !!cfg.enabled,
          pixel_id: (cfg.pixel_id || "").toString().trim(),
        });
      } catch {
        // analytics must never break the storefront
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Bootstrap fbq when config resolves & is enabled
  useEffect(() => {
    if (!ready) return;
    if (config.enabled && config.pixel_id) {
      injectPixelScript(config.pixel_id);
    }
  }, [ready, config.enabled, config.pixel_id]);

  // SPA-safe PageView on every route change
  useEffect(() => {
    if (!config.enabled || !config.pixel_id) return;
    if (lastPathRef.current === location.pathname + location.search) return;
    lastPathRef.current = location.pathname + location.search;
    if (typeof window !== "undefined" && window.fbq) {
      try { window.fbq("track", "PageView"); } catch {}
    }
  }, [location.pathname, location.search, config.enabled, config.pixel_id]);

  const track = useCallback((event, data) => {
    if (!config.enabled || !config.pixel_id) return;
    if (typeof window === "undefined" || !window.fbq) return;
    try { window.fbq("track", event, data || {}); } catch {}
  }, [config.enabled, config.pixel_id]);

  const trackCustom = useCallback((event, data) => {
    if (!config.enabled || !config.pixel_id) return;
    if (typeof window === "undefined" || !window.fbq) return;
    try { window.fbq("trackCustom", event, data || {}); } catch {}
  }, [config.enabled, config.pixel_id]);

  const value = useMemo(
    () => ({ config, ready, track, trackCustom }),
    [config, ready, track, trackCustom],
  );
  return <MetaPixelContext.Provider value={value}>{children}</MetaPixelContext.Provider>;
};

export const useMetaPixel = () => {
  const ctx = useContext(MetaPixelContext);
  if (ctx) return ctx;
  // Safe no-op fallback so consumers never crash when provider isn't mounted (e.g. admin routes if desired)
  return {
    config: { enabled: false, pixel_id: "" },
    ready: false,
    track: () => {},
    trackCustom: () => {},
  };
};

/**
 * Fire Purchase strictly once per order id (survives refresh via localStorage).
 * Call this ONLY after backend has verified payment.
 *
 * Retries for up to ~4s to handle cold-start deep-links where fbevents.js
 * hasn't finished loading yet. Sets the localStorage flag only after fbq
 * actually accepts the event.
 */
export const firePurchaseOnce = (orderId, data) => {
  if (!orderId) return;
  if (typeof window === "undefined") return;
  const flag = `hk_pixel_purchase_${orderId}`;
  try {
    if (localStorage.getItem(flag)) return;
  } catch { return; }

  // Merge orderId into payload so Meta dedupes with server-side CAPI event_id.
  const merged = { ...(data || {}) };
  let attempts = 0;
  const maxAttempts = 20; // ~4s @ 200ms
  const tick = () => {
    try {
      if (localStorage.getItem(flag)) return; // guard against a parallel caller winning first
      if (window.fbq) {
        window.fbq("track", "Purchase", merged, { eventID: orderId });
        try { localStorage.setItem(flag, "1"); } catch {}
        return;
      }
    } catch {}
    if (++attempts < maxAttempts) setTimeout(tick, 200);
  };
  tick();
};
